import type { ReportSection } from '../ReportSection.js'
import { CONSENSUS_MAX_READS } from '../../../../../Model/Order/processingProfile.js'
import { Block, bp, Caveat, Chapter, Code, num, pct, Prose, Step, Title } from './primitives.js'

const SEQUENCING_PROSE = { ILLUMINA: 'Illumina sequencing', ONT: 'Oxford Nanopore sequencing' } as const

export const qc: ReportSection = {
  id: 'qc',
  applies: () => true,
  Render: ({ data: { profile, multiQcReportImage } }) => {
    return (
      <Block>
        <Chapter>Introduction</Chapter>
        <Title>Objectives of the investigation</Title>
        <Prose>
          <p className="mb-2">Here we performed a DNA metabarcoding analysis using {SEQUENCING_PROSE[profile.platform]}.</p>
          <p className="mb-4">An initial analysis using FastQC showed that reads had the following sequence quality scores:</p>
        </Prose>
        <img src={multiQcReportImage} alt="MultiQC" className="w-3/4 mx-auto my-6 border" />
        <Prose>
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-gray-500">Figure:</span> Mean per base sequence quality for{' '}
            {profile.pairedEnd ? 'forward and reverse reads' : 'reads'} (one line per sample). For details, see MultiQC HTML reports.
          </p>
        </Prose>
      </Block>
    )
  }
}

export const rawReads: ReportSection = {
  id: 'rawReads',
  applies: () => true,
  Render: ({ data: { profile, stats } }) => (
    <Block>
      <Chapter>Results</Chapter>
      <Title>Input</Title>
      <Prose>
        <p className="mb-2">
          The order comprises <strong>{num(stats.sampleCount)}</strong> samples and <strong>{num(stats.negativeControlCount)}</strong>{' '}
          negative controls in <strong>{num(stats.poolCount)}</strong> {stats.poolCount === 1 ? 'pool' : 'pools'}.
        </p>
        {profile.pairedEnd ? (
          <p>
            The total number of sequences that came from the sequencer was <strong>{num(stats.rawReadsTotal)}</strong> read pairs, or{' '}
            <strong>{num(stats.rawReadsTotal * 2)}</strong> reads in total.
          </p>
        ) : (
          <p>
            The total number of sequences that came from the sequencer was <strong>{num(stats.rawReadsTotal)}</strong> reads.
          </p>
        )}
        {profile.demultiplexed && <Caveat>Counted after assignment of reads to samples; see demultiplexing below.</Caveat>}
      </Prose>
    </Block>
  )
}

export const demultiplexing: ReportSection = {
  id: 'demultiplexing',
  applies: (profile) => profile.demultiplexed,
  Render: () => (
    <Block>
      <Title>Demultiplexing</Title>
      <Prose>
        <p className="mb-1">
          Read pairs were assigned to samples by their inline primer pair using cutadapt with anchored, paired primers and no mismatches
          allowed; pairs shorter than 50 bp or without a matching primer pair were discarded.
        </p>
        <Caveat>The number of unassigned read pairs is not yet part of this report; the totals above count assigned pairs only.</Caveat>
      </Prose>
    </Block>
  )
}

export const merging: ReportSection = {
  id: 'merging',
  applies: (profile) => profile.mergeApplied,
  Render: ({ data: { stats } }) => (
    <Block>
      <Title>Paired-end merging</Title>
      <Step title="Paired-end merging">
        <p className="mb-1">
          Paired-end merging was performed using usearch v11.0.667 with the parameters: <br />
          <Code>-fastq_maxdiffs 99 -fastq_pctid 75 -fastq_trunctail 0</Code>
        </p>
        {stats.mergedFraction ? (
          <p>
            Across samples, a median of <strong>{pct(stats.mergedFraction.median)}</strong> of reads merged (mean{' '}
            <strong>{pct(stats.mergedFraction.mean)}</strong>, minimum <strong>{pct(stats.mergedFraction.min)}</strong>, maximum{' '}
            <strong>{pct(stats.mergedFraction.max)}</strong>).
          </p>
        ) : (
          <p>No per-sample merging statistics were recorded.</p>
        )}
      </Step>
    </Block>
  )
}

export const primerRemoval: ReportSection = {
  id: 'primerRemoval',
  applies: (profile) => profile.primerRemovalApplied,
  Render: ({ data: { profile, stats } }) => (
    <Block>
      <Title>Removing primers</Title>
      <Step title="Removing primers (trimming)">
        <p className="mb-1">
          Primers were removed using cutadapt
          {profile.primerRemoval.errorRate > 0 ? (
            <>
              {' '}
              with an error rate of <Code>{profile.primerRemoval.errorRate}</Code>
              {profile.primerRemoval.minOverlap != null && (
                <>
                  {' '}
                  and a minimum overlap of <Code>{profile.primerRemoval.minOverlap}</Code> bases
                </>
              )}
            </>
          ) : (
            ' with no mismatches allowed'
          )}
          ; reads without the primer were discarded.
        </p>
        <p>
          The primer was found in a median of <strong>{pct(stats.primerFoundForward)}</strong> of the reads (forward primer), and{' '}
          <strong>{pct(stats.primerFoundReverse)}</strong> of the remaining reads (reverse primer).
        </p>
      </Step>
    </Block>
  )
}

export const qualityFiltering: ReportSection = {
  id: 'qualityFiltering',
  applies: () => true,
  Render: ({ data: { profile, stats } }) => {
    const { minlen, maxlen, maxee, maxns } = profile.qualityFilter
    const window = maxlen ? `between ${bp(minlen)} and ${bp(maxlen)} bp` : `of at least ${bp(minlen)} bp`
    return (
      <Block>
        <Title>Quality filtering</Title>
        <Prose>
          <p className="mb-2">
            In the next step, we kept the sequences {window} in length
            {maxee != null ? ` and with a maximum of ${maxee} expected error along the length of the sequence` : ''}
            {maxns != null ? ` and without ambiguous bases` : ''}.
            {maxee == null && ' No expected-error filter is applied at nanopore error rates; consensus calling corrects errors instead.'}
          </p>
        </Prose>
        <Step title="Quality filtering">
          <p className="mb-1">
            Quality filtering was performed using vsearch 2.28.1 with the parameters:
            <br />
            <Code>
              -fastq_minlen {bp(minlen)}
              {maxlen && ` -fastq_maxlen ${bp(maxlen)}`}
              {maxee != null && ` -fastq_maxee ${maxee}`}
              {maxns != null && ` -fastq_maxns ${maxns}`}
            </Code>
          </p>
          <p>
            After quality filtering, a total of <strong>{num(stats.qualityFilteredKept)}</strong> sequences were kept, while{' '}
            <strong>{num(stats.qualityFilteredDiscarded)}</strong> were discarded.
          </p>
        </Step>
      </Block>
    )
  }
}

export const consensus: ReportSection = {
  id: 'consensus',
  applies: (profile) => profile.consensusApplied,
  Render: () => (
    <Block>
      <Title>Consensus calling and abundance recovery</Title>
      <Step title="Consensus calling">
        <p>
          The quality-filtered reads of each sample were grouped into consensus sequences with amplicon_sorter, using at most{' '}
          <strong>{num(CONSENSUS_MAX_READS)}</strong> randomly sampled reads per sample, a species similarity of 95% and a consensus
          similarity of 98%.
        </p>
      </Step>
      <Step title="Abundance recovery">
        <p>
          All quality-filtered reads of a sample were then mapped back onto its consensus sequences with minimap2; the number of mapped
          reads is the abundance of each consensus sequence, and consensus sequences no read mapped to were dropped.
        </p>
      </Step>
    </Block>
  )
}
